(function () {
  const LOADING_MESSAGES = {
    tile: [
      'We zijn gestart met je visualisatie!',
      'Neem een koffie... dit kan even duren',
      'De tegels worden in de ruimte geplaatst...',
    ],
    parquet: [
      'We zijn gestart met je visualisatie!',
      'Neem een koffie... dit kan even duren',
      'De vloer wordt in de ruimte gelegd...',
    ],
  };

  const POLL_INTERVAL_MS = 2500;
  const POLL_TIMEOUT_MS = 180000;
  const MAX_SIDE = 1280;

  function formatReset(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('nl-NL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Amsterdam',
      });
    } catch (e) {
      return '';
    }
  }

  function errorMessage(code, data) {
    switch (code) {
      case 'login_required':
        return 'Log in om deze functie te gebruiken.';
      case 'weekly_limit_reached':
        return (
          'Je hebt deze week alle ' +
          ((data && data.limit) || 5) +
          ' visualisaties gebruikt. Nieuwe visualisaties zijn weer beschikbaar op ' +
          formatReset(data && data.resetsAt) +
          '.'
        );
      case 'job_in_progress':
        return 'Er wordt al een visualisatie voor je gemaakt. Even geduld a.u.b.';
      case 'rate_limited':
        return 'Te veel verzoeken. Probeer het over enkele minuten opnieuw.';
      case 'invalid_image':
        return 'Deze foto kan niet worden gebruikt. Kies een JPG-, PNG- of WebP-foto.';
      case 'image_too_large':
        return 'Foto is te groot. Kies een kleinere foto.';
      case 'product_not_supported':
        return 'Visualisatie is niet beschikbaar voor dit product.';
      case 'failed':
        return 'De visualisatie is mislukt. Je tegoed is niet gebruikt; probeer het opnieuw.';
      case 'expired':
        return 'Deze visualisatie is verlopen. Maak een nieuwe visualisatie.';
      case 'timeout':
        return 'Het duurt langer dan verwacht. Probeer het over enkele minuten opnieuw.';
      default:
        return 'Er is iets misgegaan. Probeer het later opnieuw.';
    }
  }

  class TileVisualizer extends HTMLElement {
    connectedCallback() {
      this.endpoint = (this.dataset.endpoint || '/apps/ducci-viz').replace(/\/$/, '');
      this.productHandle = this.dataset.productHandle || '';
      this.productTitle = this.dataset.productTitle || '';
      this.kind = this.dataset.kind === 'parquet' ? 'parquet' : 'tile';
      this.loggedIn = this.dataset.loggedIn === 'true';
      this.step = 1;
      this.surface = this.kind === 'parquet' ? 'floor' : 'wall';
      this.roomDataUrl = '';
      this.roomWidth = 0;
      this.roomHeight = 0;
      this.resultUrl = '';
      this.busy = false;
      this.progressTimer = null;
      this.msgTimer = null;
      this.pollTimer = null;
      this.closeTimer = null;
      this.opener = null;
      this.scrollLocked = false;

      this.modal = this.querySelector('[data-tv-modal]');
      this.dialog = this.querySelector('[data-tv-dialog]');
      this.fileInput = this.querySelector('[data-tv-file]');
      this.preview = this.querySelector('[data-tv-preview]');
      this.previewWrap = this.querySelector('[data-tv-preview-wrap]');
      this.empty = this.querySelector('[data-tv-empty]');
      this.nextBtn = this.querySelector('[data-tv-next]');
      this.dropzone = this.querySelector('[data-tv-dropzone]');
      this.errorEl = this.querySelector('[data-tv-error]');
      this.loadingEl = this.querySelector('[data-tv-loading]');
      this.resultEl = this.querySelector('[data-tv-result]');
      this.resultImg = this.querySelector('[data-tv-result-img]');
      this.originalImg = this.querySelector('[data-tv-original-img]');
      this.progressBar = this.querySelector('[data-tv-progress]');
      this.loadingMsg = this.querySelector('[data-tv-loading-msg]');
      this.downloadLink = this.querySelector('[data-tv-download]');
      this.subtitle = this.querySelector('[data-tv-subtitle]');
      this.quotaEls = Array.prototype.slice.call(this.querySelectorAll('[data-tv-quota]'));
      this.step1Subtitle = this.subtitle ? this.subtitle.textContent : '';

      this.bindEvents();
      if (this.loggedIn) this.refreshQuota();
    }

    disconnectedCallback() {
      this.clearTimers();
      this.stopPolling();
      this.clearCloseTimer();
      this.unlockPageScroll();
      if (this.modal) {
        this.modal.classList.remove('is-open');
        this.modal.hidden = true;
      }
      this.restoreModal();
      if (this._onKeydown) {
        document.removeEventListener('keydown', this._onKeydown);
        this._onKeydown = null;
      }
    }

    /** Query modal content after it is moved to document.body */
    $all(sel) {
      if (!this.modal) return [];
      return Array.prototype.slice.call(this.modal.querySelectorAll(sel));
    }

    $(sel) {
      return this.modal ? this.modal.querySelector(sel) : null;
    }

    bindEvents() {
      this.querySelectorAll('[data-tv-open]').forEach((el) =>
        el.addEventListener('click', () => this.open())
      );
      this.$all('[data-tv-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      this.$all('[data-tv-pick]').forEach((el) =>
        el.addEventListener('click', () => this.fileInput && this.fileInput.click())
      );
      const clearBtn = this.$('[data-tv-clear]');
      if (clearBtn) clearBtn.addEventListener('click', () => this.clearRoom());
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          if (this.kind === 'parquet') this.generate();
          else this.goStep(2);
        });
      }
      const backBtn = this.$('[data-tv-back]');
      if (backBtn) backBtn.addEventListener('click', () => this.goStep(1));
      const generateBtn = this.$('[data-tv-generate]');
      if (generateBtn) generateBtn.addEventListener('click', () => this.generate());
      const retryBtn = this.$('[data-tv-retry]');
      if (retryBtn) retryBtn.addEventListener('click', () => this.retry());
      this.errorBackBtn = this.$('[data-tv-error-back]');
      if (this.errorBackBtn) this.errorBackBtn.addEventListener('click', () => this.retry());
      const shareBtn = this.$('[data-tv-share]');
      if (shareBtn) shareBtn.addEventListener('click', () => this.share());

      if (this.fileInput) {
        this.fileInput.addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          if (file) this.setRoomFile(file);
        });
      }

      if (this.dropzone) {
        ['dragenter', 'dragover'].forEach((ev) =>
          this.dropzone.addEventListener(ev, (e) => {
            e.preventDefault();
            this.dropzone.classList.add('is-dragover');
          })
        );
        ['dragleave', 'drop'].forEach((ev) =>
          this.dropzone.addEventListener(ev, (e) => {
            e.preventDefault();
            this.dropzone.classList.remove('is-dragover');
          })
        );
        this.dropzone.addEventListener('drop', (e) => {
          const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (file) this.setRoomFile(file);
        });
      }

      this.$all('[data-tv-surface]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.surface = btn.getAttribute('data-tv-surface');
          this.$all('[data-tv-surface]').forEach((b) => {
            const on = b === btn;
            b.classList.toggle('is-selected', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        });
      });

      this.$all('[data-tv-show]').forEach((btn) => {
        btn.addEventListener('click', () => this.showResultView(btn.getAttribute('data-tv-show')));
      });

      this._onKeydown = (e) => {
        if (!this.modal || this.modal.hidden) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        } else if (e.key === 'Tab') {
          this.trapFocus(e);
        }
      };
      document.addEventListener('keydown', this._onKeydown);
    }

    open() {
      if (!this.modal) return;
      this.clearCloseTimer();
      this.opener = document.activeElement;
      if (!this._modalHome) this._modalHome = this.modal.parentNode;
      if (this.modal.parentNode !== document.body) {
        document.body.appendChild(this.modal);
      }
      this.lockPageScroll();
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      if (this.busy || this.resultUrl) {
        this.goStep(3, { keepError: true });
      } else {
        this.goStep(1);
      }
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!this.modal || this.modal.hidden) return;
          this.modal.classList.add('is-open');
          const closeButton = this.$('[data-tv-close].tile-visualizer__close');
          if (closeButton) closeButton.focus({ preventScroll: true });
        });
      });
    }

    restoreModal() {
      if (!this.modal || !this._modalHome) return;
      if (this.modal.parentNode !== this._modalHome) {
        this._modalHome.appendChild(this.modal);
      }
    }

    close() {
      if (!this.modal || this.modal.hidden) return;
      this.clearCloseTimer();
      this.modal.classList.remove('is-open');
      const finishClose = () => {
        if (!this.modal) return;
        this.modal.setAttribute('aria-hidden', 'true');
        this.modal.hidden = true;
        this.unlockPageScroll();
        if (this.opener && typeof this.opener.focus === 'function') {
          this.opener.focus({ preventScroll: true });
        }
        this.opener = null;
        this.closeTimer = null;
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finishClose();
      } else {
        this.closeTimer = window.setTimeout(finishClose, 230);
      }
    }

    clearCloseTimer() {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    lockPageScroll() {
      if (this.scrollLocked) return;
      const bodyStyle = window.getComputedStyle(document.body);
      const bodyPadding = parseFloat(bodyStyle.paddingRight) || 0;
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.body.style.setProperty('--tv-body-padding-right', bodyPadding + 'px');
      document.body.style.setProperty('--tv-scrollbar-width', scrollbarWidth + 'px');
      document.body.classList.add('tile-visualizer-open');
      this.scrollLocked = true;
    }

    unlockPageScroll() {
      if (!this.scrollLocked) return;
      document.body.classList.remove('tile-visualizer-open');
      document.body.style.removeProperty('--tv-body-padding-right');
      document.body.style.removeProperty('--tv-scrollbar-width');
      this.scrollLocked = false;
    }

    trapFocus(event) {
      const focusable = this.$all(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (!focusable.length) {
        event.preventDefault();
        if (this.dialog) this.dialog.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    goStep(n, options) {
      this.step = n;
      this.$all('[data-tv-view]').forEach((el) => {
        el.hidden = String(el.getAttribute('data-tv-view')) !== String(n);
      });
      this.$all('[data-tv-dot]').forEach((dot) => {
        const d = parseInt(dot.getAttribute('data-tv-dot'), 10);
        dot.classList.toggle('is-active', d <= n);
      });
      if (this.subtitle) {
        if (n === 1) this.subtitle.textContent = this.step1Subtitle;
        else if (n === 2) this.subtitle.textContent = 'Kies waar je de tegels wilt gebruiken';
        else this.subtitle.textContent = '';
      }
      if (this.modal && !this.modal.hidden) {
        this.modal.scrollTo({
          top: 0,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
      if (!(options && options.keepError)) this.setError('');
      if (this.errorBackBtn && !(options && options.keepError)) this.errorBackBtn.hidden = true;
    }

    setRoomFile(file) {
      if (!file || !/^image\/(jpeg|png|webp)$/.test(file.type || '')) {
        this.setError('Kies een JPG-, PNG- of WebP-foto.');
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        this.setError('Foto is te groot (max. 15 MB).');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.compressImage(String(reader.result), MAX_SIDE)
          .then((img) => {
            this.roomDataUrl = img.dataUrl;
            this.roomWidth = img.width;
            this.roomHeight = img.height;
            if (this.preview) this.preview.src = img.dataUrl;
            if (this.originalImg) this.originalImg.src = img.dataUrl;
            if (this.empty) this.empty.hidden = true;
            if (this.previewWrap) this.previewWrap.hidden = false;
            if (this.nextBtn) this.nextBtn.disabled = false;
            this.setError('');
          })
          .catch(() => this.setError('Foto kon niet worden verwerkt.'));
      };
      reader.onerror = () => this.setError('Foto kon niet worden gelezen.');
      reader.readAsDataURL(file);
    }

    clearRoom() {
      this.roomDataUrl = '';
      this.roomWidth = 0;
      this.roomHeight = 0;
      if (this.fileInput) this.fileInput.value = '';
      if (this.preview) this.preview.removeAttribute('src');
      if (this.empty) this.empty.hidden = false;
      if (this.previewWrap) this.previewWrap.hidden = true;
      if (this.nextBtn) this.nextBtn.disabled = true;
    }

    compressImage(dataUrl, maxSide) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          let w = img.width;
          let h = img.height;
          const scale = Math.min(1, maxSide / Math.max(w, h));
          w = Math.round(w * scale);
          h = Math.round(h * scale);
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('canvas'));
          ctx.drawImage(img, 0, 0, w, h);
          resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.86), width: w, height: h });
        };
        img.onerror = reject;
        img.src = dataUrl;
      });
    }

    async request(path, options) {
      const res = await fetch(this.endpoint + path, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...options,
      });
      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        data = null;
      }
      return { status: res.status, ok: res.ok, data: data || {} };
    }

    async refreshQuota() {
      try {
        const { ok, data } = await this.request('/quota', { method: 'GET' });
        if (ok) this.renderQuota(data);
      } catch (e) {
        /* quota is informational; the server enforces the limit */
      }
    }

    renderQuota(data) {
      if (!data || typeof data.remaining !== 'number') return;
      const text =
        data.remaining > 0
          ? 'Nog ' + data.remaining + ' van ' + data.limit + ' visualisaties deze week'
          : 'Weeklimiet bereikt. Weer beschikbaar op ' + formatReset(data.resetsAt);
      this.quotaEls.forEach((el) => {
        el.textContent = text;
        el.classList.toggle('is-empty', data.remaining <= 0);
        el.hidden = false;
      });
    }

    async generate() {
      if (this.busy) return;
      if (!this.roomDataUrl) {
        this.setError('Upload eerst een ruimtefoto.');
        this.goStep(1);
        return;
      }

      this.busy = true;
      this.resultUrl = '';
      this.goStep(3);
      if (this.loadingEl) this.loadingEl.hidden = false;
      if (this.resultEl) this.resultEl.hidden = true;
      this.startProgress();

      try {
        const { status, data } = await this.request('/generate', {
          method: 'POST',
          body: JSON.stringify({
            handle: this.productHandle,
            surface: this.surface,
            roomImage: this.roomDataUrl,
            roomWidth: this.roomWidth,
            roomHeight: this.roomHeight,
          }),
        });

        this.renderQuota(data);
        if (status !== 202 || !data.jobId) {
          this.fail(data.error || 'server_error', data);
          return;
        }
        this.poll(data.jobId, Date.now());
      } catch (err) {
        this.fail('network');
      }
    }

    poll(jobId, startedAt) {
      this.stopPolling();
      this.pollTimer = window.setTimeout(async () => {
        this.pollTimer = null;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          this.fail('timeout');
          return;
        }
        try {
          const { ok, data } = await this.request('/jobs/' + encodeURIComponent(jobId), { method: 'GET' });
          if (!ok) {
            this.fail(data.error || 'server_error', data);
            return;
          }
          this.renderQuota(data);
          if (data.status === 'pending') {
            this.poll(jobId, startedAt);
          } else if (data.status === 'done' && data.image) {
            this.busy = false;
            this.showResult(data.image);
          } else {
            this.fail(data.status || 'server_error', data);
          }
        } catch (e) {
          this.poll(jobId, startedAt);
        }
      }, POLL_INTERVAL_MS);
    }

    stopPolling() {
      if (this.pollTimer) window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }

    fail(code, data) {
      this.busy = false;
      this.stopPolling();
      this.clearTimers();
      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.errorBackBtn) this.errorBackBtn.hidden = false;
      this.setError(errorMessage(code, data));
      if (code === 'weekly_limit_reached' || code === 'failed') this.refreshQuota();
    }

    showResult(url) {
      this.clearTimers();
      this.resultUrl = url;
      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.resultEl) this.resultEl.hidden = false;
      if (this.resultImg) {
        this.resultImg.src = url;
        this.resultImg.hidden = false;
      }
      if (this.originalImg) this.originalImg.hidden = true;
      if (this.downloadLink) {
        this.downloadLink.href = url;
        this.downloadLink.setAttribute('download', 'visualisatie.jpg');
      }
      this.setError('');
      this.showResultView('generated');
    }

    showResultView(mode) {
      const generated = mode === 'generated';
      if (this.resultImg) this.resultImg.hidden = !generated;
      if (this.originalImg) this.originalImg.hidden = generated;
      this.$all('[data-tv-show]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.getAttribute('data-tv-show') === mode);
      });
    }

    startProgress() {
      this.clearTimers();
      const messages = LOADING_MESSAGES[this.kind];
      let pct = 5;
      let msgIdx = 0;
      if (this.progressBar) this.progressBar.style.width = pct + '%';
      if (this.loadingMsg) this.loadingMsg.textContent = messages[0];

      this.progressTimer = setInterval(() => {
        pct = Math.min(92, pct + Math.random() * 4);
        if (this.progressBar) this.progressBar.style.width = pct + '%';
      }, 1500);

      this.msgTimer = setInterval(() => {
        msgIdx = (msgIdx + 1) % messages.length;
        if (this.loadingMsg) this.loadingMsg.textContent = messages[msgIdx];
      }, 8000);
    }

    clearTimers() {
      if (this.progressTimer) clearInterval(this.progressTimer);
      if (this.msgTimer) clearInterval(this.msgTimer);
      this.progressTimer = null;
      this.msgTimer = null;
      if (this.progressBar) this.progressBar.style.width = '100%';
    }

    retry() {
      if (this.busy) return;
      this.resultUrl = '';
      this.goStep(1);
      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.resultEl) this.resultEl.hidden = true;
    }

    async share() {
      if (!this.resultUrl) return;
      try {
        const blob = await (await fetch(this.resultUrl)).blob();
        const file = new File([blob], 'visualisatie.jpg', { type: blob.type || 'image/jpeg' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: this.productTitle || 'Visualisatie',
            text: this.kind === 'parquet' ? 'Zo ziet deze vloer eruit in mijn ruimte' : 'Zo zien deze tegels eruit in mijn ruimte',
            files: [file],
          });
          return;
        }
        if (navigator.share) {
          await navigator.share({ title: this.productTitle || 'Visualisatie', url: window.location.href });
          return;
        }
        if (this.downloadLink) this.downloadLink.click();
      } catch (e) {
        /* user cancelled */
      }
    }

    setError(message) {
      if (!this.errorEl) return;
      if (message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      } else {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }
  }

  if (!customElements.get('tile-visualizer')) {
    customElements.define('tile-visualizer', TileVisualizer);
  }
})();
